import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  getMyInsurance,
  submitInsurance,
  getInsuranceDownloadUrl,
} from "@/lib/insurance.functions";
import { getMyRtw } from "@/lib/rtw.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReserveHeader } from "@/components/ReserveHeader";
import { RtwCheckCard } from "@/components/RtwCheckCard";

export const Route = createFileRoute("/_authenticated/bartender/documents")({
  head: () => ({
    meta: [
      { title: "Document Centre — The Hospitality Reserve" },
      {
        name: "description",
        content:
          "Upload and manage your Right to Work and public liability insurance documents in one place.",
      },
    ],
  }),
  component: DocumentsPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-destructive">Error: {error.message}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

type ExpiryState = {
  label: string;
  tone: "ok" | "warn" | "danger" | "muted";
  days: number | null;
};

function expiryState(expiresAt: string | null | undefined): ExpiryState {
  if (!expiresAt) return { label: "No expiry on file", tone: "muted", days: null };
  const now = Date.now();
  const exp = new Date(expiresAt).getTime();
  const days = Math.floor((exp - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`, tone: "danger", days };
  if (days <= 7) return { label: `Expires in ${days} day${days === 1 ? "" : "s"}`, tone: "danger", days };
  if (days <= 30) return { label: `Expires in ${days} days`, tone: "warn", days };
  return { label: `Valid · expires in ${days} days`, tone: "ok", days };
}

function toneClass(tone: ExpiryState["tone"]) {
  switch (tone) {
    case "ok": return "text-emerald-500 border-emerald-500/40";
    case "warn": return "text-amber-500 border-amber-500/40";
    case "danger": return "text-destructive border-destructive/40";
    default: return "text-muted-foreground border-border";
  }
}

function fmt(d: string | null | undefined) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return d; }
}

function DocumentsPage() {
  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Compliance</p>
        <h1 className="text-3xl font-light">Document Centre</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          One place to upload, review, and track the expiry of your Right to Work
          evidence and public liability insurance. Keep both verified to remain
          eligible to accept shifts.
        </p>

        <section className="mt-10">
          <h2 className="font-display text-lg">Right to Work</h2>
          <RtwWithExpiry />
        </section>

        <section className="mt-12">
          <h2 className="font-display text-lg">Public liability insurance</h2>
          <InsuranceCard />
        </section>

        <p className="mt-10 text-xs text-muted-foreground">
          Need help? <a href="mailto:support@thehospitalityreserve.com" className="underline">Contact support</a>.
        </p>
      </main>
    </div>
  );
}

function RtwWithExpiry() {
  const { data: rtw } = useQuery({ queryKey: ["rtw", "mine"], queryFn: () => getMyRtw() });
  const exp = expiryState(rtw?.expires_at ?? null);
  const showExpiry = rtw?.status === "verified";

  return (
    <div>
      {showExpiry && (
        <div className={`mt-3 inline-flex items-center border px-3 py-1 text-[11px] tracking-wide-luxury ${toneClass(exp.tone)}`}>
          {exp.label}
        </div>
      )}
      <RtwCheckCard />
    </div>
  );
}

function InsuranceCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["insurance", "mine"], queryFn: () => getMyInsurance() });
  const ins = data?.insurance ?? null;

  const [provider, setProvider] = useState("");
  const [policy, setPolicy] = useState("");
  const [expires, setExpires] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const mut = useMutation({
    mutationFn: submitInsurance,
    onSuccess: (res) => {
      if ("error" in res) {
        toast.error(res.error);
      } else {
        toast.success("Insurance submitted for review.");
        qc.invalidateQueries({ queryKey: ["insurance", "mine"] });
        setProvider(""); setPolicy(""); setExpires(""); setFile(null);
      }
    },
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { toast.error("Please attach your insurance certificate."); return; }
    setUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) throw new Error("Not signed in");
      const ext = file.name.split(".").pop() || "pdf";
      const path = `${user.id}/policy-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("insurance-documents")
        .upload(path, file, { contentType: file.type || "application/pdf", upsert: false });
      if (error) throw error;
      await mut.mutateAsync({ data: { provider, policy_number: policy, expires_at: expires, document_path: path } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function openDoc(path: string) {
    const res = await getInsuranceDownloadUrl({ data: { path } });
    if ("error" in res) { toast.error(res.error); return; }
    window.open(res.url, "_blank", "noopener,noreferrer");
  }

  const status = (ins?.insurance_status as string | undefined) ?? "not_submitted";
  const exp = expiryState(ins?.insurance_expires_at as string | null | undefined);
  const statusBadge =
    status === "verified" ? { label: "Verified", cls: "text-emerald-500 border-emerald-500/40" }
    : status === "submitted" ? { label: "In review", cls: "text-amber-500 border-amber-500/40" }
    : status === "rejected" ? { label: "Action required", cls: "text-destructive border-destructive/40" }
    : { label: "Not submitted", cls: "text-muted-foreground border-border" };

  return (
    <div className="mt-3">
      <div className="border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-display text-lg">Current policy</p>
            <p className="mt-1 max-w-md text-xs text-muted-foreground leading-relaxed">
              As a self-employed contractor you must hold valid public liability
              insurance. Upload your certificate and we'll keep it on file until
              expiry.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`shrink-0 border px-3 py-1 text-[10px] tracking-wide-luxury ${statusBadge.cls}`}>
              {statusBadge.label}
            </span>
            {status === "verified" && (
              <span className={`shrink-0 border px-3 py-1 text-[10px] tracking-wide-luxury ${toneClass(exp.tone)}`}>
                {exp.label}
              </span>
            )}
          </div>
        </div>

        {ins?.insurance_provider && (
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <dt className="text-muted-foreground">Provider</dt><dd>{ins.insurance_provider}</dd>
            <dt className="text-muted-foreground">Policy number</dt><dd className="font-mono text-xs">{ins.insurance_policy_number}</dd>
            <dt className="text-muted-foreground">Expires</dt><dd>{fmt(ins.insurance_expires_at as string | null)}</dd>
            {ins.insurance_verified_at && (
              <>
                <dt className="text-muted-foreground">Verified</dt>
                <dd>{fmt(ins.insurance_verified_at as string | null)}</dd>
              </>
            )}
          </dl>
        )}

        {ins?.insurance_notes && (
          <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
            Admin note: {ins.insurance_notes}
          </p>
        )}

        {ins?.insurance_document_path && (
          <Button variant="outline" size="sm" className="mt-4"
            onClick={() => openDoc(ins.insurance_document_path as string)}>
            View uploaded certificate
          </Button>
        )}
      </div>

      <form onSubmit={onSubmit} className="mt-4 space-y-4 border border-border p-5">
        <p className="text-[11px] uppercase tracking-wide-luxury text-muted-foreground">
          {ins?.insurance_provider ? "Update / renew" : "Submit for review"}
        </p>
        <div>
          <Label>Insurer</Label>
          <Input required value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="e.g. Hiscox" />
        </div>
        <div>
          <Label>Policy number</Label>
          <Input required value={policy} onChange={(e) => setPolicy(e.target.value)} />
        </div>
        <div>
          <Label>Expiry date</Label>
          <Input required type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </div>
        <div>
          <Label>Certificate (PDF or image)</Label>
          <Input required type="file" accept="application/pdf,image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </div>
        <Button type="submit" disabled={uploading || mut.isPending}>
          {uploading || mut.isPending ? "Uploading…" : "Submit for review"}
        </Button>
        <p className="text-[10px] text-muted-foreground">
          Need cover? <a href="https://www.policybee.co.uk" target="_blank" rel="noopener noreferrer" className="underline">PolicyBee</a> offers
          tailored hospitality public liability policies.
        </p>
      </form>
    </div>
  );
}
